//React
import { useState, useEffect } from 'react';

//System
import { register, setState, getState } from '../../system/stateManager';

//System Helpers
import { setup as setupBackButton, performBack } from '../../helpers/backButton';
import iwdGet from '../../helpers/iwdGet';

//Styles
import './styles.css';

//Modal Component
const ErrorModal = ({ error, onClose }) => {
	if (!error) return null;

	return (
		<div className='error-modal-overlay' onClick={onClose}>
			<div className='error-modal' onClick={e => e.stopPropagation()}>
				<div className='modal-header'>
					<h3>Error Details</h3>
					<button className='close-btn' onClick={onClose}>×</button>
				</div>
				<div className='modal-body'>
					<div className='modal-section'>
						<strong>Error:</strong>
						<pre>{error.error}</pre>
					</div>
					<div className='modal-section'>
						<strong>Stack:</strong>
						<pre>{error.stack}</pre>
					</div>
					<div className='modal-section'>
						<strong>Info:</strong>
						<pre>{JSON.stringify(error.info, null, 2)}</pre>
					</div>
					<div className='modal-section'>
						<strong>ID:</strong> {error.id}
					</div>
					<div className='modal-section'>
						<strong>Source Module:</strong> {error.sourceModule}
					</div>
					<div className='modal-section'>
						<strong>Source Method:</strong> {error.sourceMethod}
					</div>
					<div className='modal-section'>
						<strong>Date:</strong> {(() => {
							if (error.date && typeof error.date === 'string') {
								return new Date(error.date).toLocaleString();
							} else if (error.date && error.date.epoch_time) {
								return new Date(error.date.epoch_time * 1000).toLocaleString();
							} else {
								return 'Invalid Date';
							}
						})()}
					</div>
				</div>
			</div>
		</div>
	);
};

const onClickRefresh = (forceRefresh = false) => {
	const { needsRefresh, pageSize = 100, currentPage = 0 } = getState('errorLog');
	if (!needsRefresh && !forceRefresh)
		return;

	(async () => {
		let { list, count } = await iwdGet('mtErrorLog', {
			offset: currentPage * pageSize,
			limit: pageSize
		});

		const totalPages = count ? Math.ceil(count / pageSize) : null;

		setState('errorLog', {
			list,
			needsRefresh: false,
			hasNextPage: list.length === pageSize,
			totalCount: count || null,
			totalPages
		});
	})();
};

const onPageChange = (direction) => {
	const { pageSize = 100, currentPage = 0, list = [] } = getState('errorLog');

	let newPage = currentPage + direction;

	// Prevent going to negative pages
	if (newPage < 0) newPage = 0;

	setState('errorLog', {
		currentPage: newPage,
		needsRefresh: true
	});
};

const onPageNumberClick = (pageNumber) => {
	setState('errorLog', {
		currentPage: pageNumber,
		needsRefresh: true
	});
};

//Components
const ErrorLog = () => {
	const [state, setter] = useState({ vis: false });
	const [modalError, setModalError] = useState(null);

	useEffect(register.bind(null, 'errorLog', setter, state), [setter]);
	useEffect(onClickRefresh, [state.needsRefresh]);

	const openModal = (error) => {
		setModalError(error);
	};

	const closeModal = () => {
		setModalError(null);
	};

	useEffect(() => {
		if (!modalError) return;

		const handleKeyDown = (e) => {
			if (e.key === 'Escape') {
				closeModal();
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [modalError]);

	if (!state.vis)
		return null;

	return (
		<div className='cpnErrorLog centered'>
			<div className='title'>
				Error Log - Page {state.currentPage + 1}
			</div>
			<div className='bottom'>
				<div className='headings'>
					<div className='entry wide'>Error</div>
					<div className='entry wide'>Source Module</div>
					<div className='entry'>Source Method</div>
					<div className='entry narrow'>Date</div>
					<div className='entry narrow'>Details</div>
				</div>
				<div className='list'>
					{(state.list || []).map((error, i) => {
						let date;
						if (error.date && typeof error.date === 'string') {
							date = new Date(error.date).toLocaleString();
						} else if (error.date && error.date.epoch_time) {
							date = new Date(error.date.epoch_time * 1000).toLocaleString();
						} else {
							date = 'Invalid Date';
						}

						const errorFirstLine = error.error.split('\n')[0];
						const key = `error-${i}`;

						let className = 'row';
						if (i % 2 === 0)
							className += ' even';
						else
							className += ' odd';

						return (
							<div key={key} className={className}>
								<div className='entry wide error-text'>{errorFirstLine}</div>
								<div className='entry wide'>{error.sourceModule}</div>
								<div className='entry'>{error.sourceMethod}</div>
								<div className='entry narrow'>{date}</div>
								<div className='entry narrow'>
									<button className='details-btn' onClick={() => openModal(error)}>View</button>
								</div>
							</div>
						);
					})}
				</div>
			</div>
			<div className='toolbar'>
				<div className='btn gray' onClick={performBack}>
					Back
				</div>
				<div
					className={`btn first-btn${(state.currentPage || 0) === 0 ? ' disabled' : ''}`}
					onClick={() => onPageNumberClick(0)}
				>
					First
				</div>
				<div
					className={`btn${(state.currentPage || 0) === 0 ? ' disabled' : ''}`}
					onClick={() => onPageChange(-1)}
				>
					Previous
				</div>
				{/* Page number buttons */}
				{(() => {
					const currentPage = state.currentPage || 0;
					const totalPages = state.totalPages;
					const pageButtons = [];
					const startPage = Math.max(0, currentPage - 2);
					const endPage = totalPages ? Math.min(totalPages - 1, currentPage + 2) : currentPage + 2;

					for (let i = startPage; i <= endPage; i++) {
						pageButtons.push(
							<div
								key={`page-${i}`}
								className={`btn page-btn${i === currentPage ? ' current' : ''}`}
								onClick={() => onPageNumberClick(i)}
							>
								{i + 1}
							</div>
						);
					}

					return pageButtons;
				})()}
				<div
					className={`btn${state.hasNextPage === false ? ' disabled' : ''}`}
					onClick={() => onPageChange(1)}
				>
					Next
				</div>
				<div
					className={`btn${state.totalPages ? ((state.currentPage || 0) === state.totalPages - 1 ? ' disabled' : '') : ' disabled'}`}
					onClick={() => state.totalPages ? onPageNumberClick(state.totalPages - 1) : null}
				>
					Last
				</div>
				<div className='btn right' onClick={onClickRefresh.bind(null, true)}>
					Refresh List
				</div>
			</div>
			<ErrorModal error={modalError} onClose={closeModal} />
		</div>
	);
};

export default ErrorLog;
